<template>
    <ListingTableFilter
        :filter="filter"
        :valuesHumanized="appliedFilterLabels"
        :filterConfig="filterConfig"
        type="popover"
        @updateFilter="updateFilter"
        @updateVisibility="updateVisibility"
        @removeFilter="removeFilter"
    >
        <template #filterDropdownContent>
            <CompassInput
                ref="compassMultipleSelectInput"
                :modelValue="query"
                class="listing-filter-popover-users__search"
                :placeholder="labels.filterablePlaceholder"
                prefixIcon="search"
                isEraseable
                @update:modelValue="handleInput"
                @keydown.esc.stop.prevent="updateVisibility(false)"
                @keydown.tab="updateVisibility(false)"
            />

            <div class="listing-filter-popover-users__content">
                <CompassEmptyState
                    v-if="shouldDisplayNoResult"
                    illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                    :caption="labels.noResultsLabel"
                />
                <div v-else class="listing-filter-popover-users__content--options">
                    <CompassCheckbox
                        v-if="!shouldDisplayNoResult"
                        modelValue="checkAll"
                        :label="labels.checkAll"
                        :indeterminate="checkboxAllIsIndeterminate"
                        :checked="checkboxAllIsChecked"
                        @change="onCheckAll"
                    />
                    <template v-if="query !== ''">
                        <CompassCheckbox
                            v-for="option in optionsFiltered"
                            :key="option.value"
                            :modelValue="option.value"
                            :checked="allOptionsChecked.includes(option.value)"
                            :showLabel="false"
                            @change="onCheckboxChecked(option.value)"
                        >
                            <div class="listing-filter-checkbox__label">
                                <CompassAvatar
                                    :initials="option.avatar.initials"
                                    size="extra-small"
                                    :type="option.avatar.type"
                                    :photo="option.avatar.avatar"
                                    :colorType="getAvatarColor(option)"
                                />
                                <span class="typographies-paragraph-medium-regular">
                                    {{ option.label }}
                                </span>
                            </div>
                        </CompassCheckbox>
                    </template>

                    <CompassCheckboxGroup
                        v-for="group in filter.options"
                        v-else
                        :key="group.id"
                        :label="group.name"
                        :value="
                            allOptionsChecked.filter((option) =>
                                getStaffsId(group.options).includes(option),
                            )
                        "
                        checkAll
                        :options="getStaffsId(group.options)"
                        @onCheckAll="
                            (checked) => onCheckAllGroup(checked, getStaffsId(group.options))
                        "
                    >
                        <CompassCheckbox
                            v-for="option in group.options"
                            :key="option.value"
                            :checked="allOptionsChecked.includes(option.value)"
                            :modelValue="option.value"
                            :showLabel="false"
                            @change="onCheckboxChecked(option.value)"
                        >
                            <div class="listing-filter-checkbox__label">
                                <CompassAvatar
                                    :initials="option.avatar.initials"
                                    size="extra-small"
                                    :type="option.avatar.type"
                                    :photo="option.avatar.avatar"
                                    :colorType="getAvatarColor(option)"
                                />
                                <span class="typographies-paragraph-medium-regular">
                                    {{ option.label }}
                                </span>
                            </div>
                        </CompassCheckbox>
                    </CompassCheckboxGroup>
                </div>
            </div>
            <div
                v-if="!shouldDisplayNoResult"
                class="listing-filter-popover-users__applied-button-wrapper"
            >
                <button
                    class="listing-filter-popover-users__applied-button typographies-paragraph-medium-semibold"
                    @click="updateFilter"
                >
                    {{ $t.__('common.actions.apply') }}
                </button>
            </div>
        </template>
    </ListingTableFilter>
</template>
<script>
import ListingTableFilter from 'vcomponents/common/listing/filters/ListingTableFilter.vue';
import { concat, union, uniq } from 'lodash';
import { randAvatarColorFullname } from 'utils/avatar';
import ActivitiesListingFiltersMixins from 'mixins/activities/activitiesListingFiltersMixins.js';

export default {
    components: { ListingTableFilter },
    mixins: [ActivitiesListingFiltersMixins],
    props: {
        filter: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            allOptionsChecked: [],
            labels: {
                checkAll: this.$t.__('common.actions.check_all'),
                filterablePlaceholder: this.$t.__('common.actions.search'),
                noResultsLabel: this.$t.__('common.labels.no_result'),
            },
            dropdown: null,
            query: '',
            optionsFiltered: [],
            displayPopover: false,
        };
    },
    computed: {
        shouldDisplayNoResult() {
            return this.query !== '' && this.optionsFiltered.length === 0;
        },
        appliedFilterLabels() {
            const namesStaff = [];
            for (const staff of this.filter.optionsSelected) {
                for (const group of this.filter.options) {
                    const option = group.options.find((item) => item.value === staff);
                    if (option) {
                        namesStaff.push(option.label);
                        break;
                    }
                }
            }
            return namesStaff.join(', ');
        },
        filterConfig() {
            return {
                isVisible: this.filter.displayDropdown,
                customClass: 'listing-filter-popover-users',
            };
        },
        allOptionsValues() {
            let allOptions = [];
            for (const group of this.filter.options) {
                allOptions = concat(
                    allOptions,
                    group.options.map((option) => option.value),
                );
            }
            return uniq(allOptions);
        },
        checkboxAllIsIndeterminate() {
            if (this.query !== '') {
                for (const optionFiltered of this.optionsFiltered) {
                    if (this.allOptionsChecked.includes(optionFiltered.value)) {
                        return true;
                    }
                }
                return false;
            }
            return (
                this.allOptionsChecked.length > 0 &&
                this.allOptionsChecked.length < this.allOptionsValues.length
            );
        },
        checkboxAllIsChecked() {
            if (this.query !== '') {
                return this.optionsFiltered.every((item) =>
                    this.allOptionsChecked.includes(item.value),
                );
            }
            return this.allOptionsChecked.length === this.allOptionsValues.length;
        },
    },
    watch: {
        'filter.optionsSelected': {
            handler() {
                this.allOptionsChecked = this.filter.optionsSelected;
            },
        },
    },
    created() {
        this.allOptionsChecked = this.filter.optionsSelected;
    },
    methods: {
        getAvatarColor(option) {
            return randAvatarColorFullname(option.label);
        },
        getStaffsId(options) {
            return options.map((option) => option.value);
        },
        onCheckAllGroup(checked, options) {
            this.allOptionsChecked = this.allOptionsChecked.filter(
                (option) => !options.includes(option),
            );
            if (checked) {
                this.allOptionsChecked = union(this.allOptionsChecked, options);
            }
        },
        onCheckboxChecked(value) {
            if (this.allOptionsChecked.includes(value)) {
                this.allOptionsChecked = this.allOptionsChecked.filter(
                    (optionChecked) => optionChecked !== value,
                );
            } else {
                this.allOptionsChecked = [...this.allOptionsChecked, value];
            }
        },
        onCheckAll(checked) {
            this.allOptionsChecked = checked ? this.allOptionsValues : [];
        },
        updateFilter() {
            this.updateVisibility(false);
            this.$emit('updateFilter', this.allOptionsChecked);
        },
        removeFilter() {
            this.$emit('removeFilter', this.filter.code);
        },
        updateVisibility(visibility) {
            this.query = '';
            this.$emit('update:displayDropdown', visibility);
            this.displayPopover = visibility;
        },
        handleInput(value) {
            this.query = value;
            this.filterOptions();
        },
        filterOptions() {
            this.optionsFiltered = [];
            for (const group of this.filter.options) {
                const optionFiltered = group.options.filter((option) =>
                    option.label.toLowerCase().includes(this.query.toLowerCase()),
                );
                this.optionsFiltered = uniq(concat(this.optionsFiltered, optionFiltered));
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.listing-filter-checkbox__label {
    align-items: center;
    display: flex;

    .compass-avatar {
        margin-right: var(--spacing-1);
    }
}
</style>
<style lang="scss">
#compass-popover-container {
    .listing-filter-popover-users {
        max-width: 260px;

        .compass-popover__content {
            padding: 0;
        }

        &__search {
            margin: var(--spacing-1) var(--spacing-1) var(--spacing-0) var(--spacing-1);
        }

        &__content {
            max-height: 321px;
            overflow-y: auto;
            padding: var(--spacing-1) var(--spacing-3);

            &--options {
                display: flex;
                flex-direction: column;
                gap: var(--spacing-2);

                .compass-checkbox__no-label--medium > label {
                    height: auto;
                }
            }
        }

        .listing-filter-popover-users__applied-button-wrapper {
            border-top: 1px solid var(--colors-common-border-default);
            padding: var(--spacing-1);
        }

        .listing-filter-popover-users__applied-button {
            background: var(--colors-common-background-default);
            border: none;
            border-radius: var(--spacing-0);
            color: var(--colors-common-text-primary-default);
            cursor: pointer;
            padding: var(--spacing-1);
            text-align: start;
            width: 100%;

            &:hover {
                background: var(--colors-component-background-actionable-hover);
            }

            .compass-button__content {
                justify-content: start;
            }
        }
    }
}
</style>
